import { useTranslation } from 'react-i18next'
import { TNavigationLocales } from 'src/types/locales'
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import {
  CurecallButton,
  CurecallCheckbox,
  CurecallInput,
  CurecallList,
  CurecallListHeaders,
  CurecallPaginationBar,
  CurecallSearchInput,
  ECurecallButtonAppearance,
  ECurecallSize,
} from '@honestica/curecall-ui'
import { THRContact } from 'src/types/hr'

const ITEMS_PER_PAGE = 50

type TOption = { label: string; value: string }

type TContactHeaderName = 'Selected' | 'Lastname' | 'Firstname' | 'Role' | 'ServiceUnit'
type TContactRow = { [key in TContactHeaderName]: ReactNode } & { _isNew: boolean }
type TSortState = { key: TContactHeaderName; descOrder: boolean }

type TFilterListProps = Readonly<{
  label: string
  options: TOption[]
  selectedOptions: TOption[]
  searchPlaceholder: string
  onToggle: (option: TOption) => void
}>

const FilterList = ({ label, options, selectedOptions, searchPlaceholder, onToggle }: TFilterListProps) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')

  const normalizeStr = (str: string) => str.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

  const filteredOptions = useMemo(() => {
    const query = normalizeStr(search.trim())
    if (query === '') return options
    return options.filter((o) => normalizeStr(o.label).includes(query))
  }, [options, search])

  const selectedSet = new Set(selectedOptions.map((o) => o.value))

  return (
    <div className="c-flex c-flex-col c-gap-1.5 c-flex-1 c-min-h-0">
      <span className="c-text-xs c-font-semibold c-text-grey-600 c-shrink-0">{label}</span>
      <div className="c-shrink-0">
        <CurecallSearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClear={() => setSearch('')}
          placeholder={searchPlaceholder}
          searchInputAriaLabel={searchPlaceholder}
          clearButtonAriaLabel={t('pages.hr.campaigns.new.contacts.filter-search-clear')}
        />
      </div>
      <div className="c-overflow-y-auto c-flex-1 c-border c-border-grey-200 c-rounded-lg">
        {filteredOptions.map((option) => (
          <div
            key={option.value}
            className="c-px-3 c-py-1 hover:c-bg-grey-50 c-border-b c-border-grey-100 last:c-border-0 c-cursor-pointer"
            onClick={() => onToggle(option)}
          >
            <CurecallCheckbox
              label={option.label}
              checked={selectedSet.has(option.value)}
              onChange={() => onToggle(option)}
            />
          </div>
        ))}
        {filteredOptions.length === 0 && (
          <p className="c-px-3 c-py-2 c-text-sm c-text-grey-400 c-italic">—</p>
        )}
      </div>
    </div>
  )
}

type TProps = Readonly<{
  contacts: THRContact[]
  initialSelectedIds: number[]
  initialRoleValues: string[]
  initialServiceUnitValues: string[]
  onNext: (selectedIds: number[], selectedRoleValues: string[], selectedServiceUnitValues: string[]) => void
  onBack: () => void
}>

export const StepContactSelection = ({
  contacts,
  initialSelectedIds,
  initialRoleValues,
  initialServiceUnitValues,
  onNext,
  onBack,
}: TProps) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.language as TNavigationLocales

  const roleOptions = useMemo<TOption[]>(() => {
    const options = [...new Set(contacts.map((c) => c.role).filter(Boolean) as string[])]
      .sort((a, b) => a.localeCompare(b, locale))
      .map((role) => ({ label: role, value: role }))
    if (contacts.some((c) => !c.role)) {
      options.unshift({ label: t('pages.hr.campaigns.new.contacts.filter-not-specified'), value: '' })
    }
    return options
  }, [contacts, t, locale])

  const serviceUnitOptions = useMemo<TOption[]>(() => {
    const options = [...new Set(contacts.map((c) => c.serviceUnit).filter(Boolean) as string[])]
      .sort((a, b) => a.localeCompare(b, locale))
      .map((unit) => ({ label: unit, value: unit }))
    if (contacts.some((c) => !c.serviceUnit)) {
      options.unshift({ label: t('pages.hr.campaigns.new.contacts.filter-not-specified'), value: '' })
    }
    return options
  }, [contacts, t, locale])

  const [selectedRoleOptions, setSelectedRoleOptions] = useState<TOption[]>(() =>
    roleOptions.filter((o) => initialRoleValues.includes(o.value)),
  )
  const [selectedServiceUnitOptions, setSelectedServiceUnitOptions] = useState<TOption[]>(() =>
    serviceUnitOptions.filter((o) => initialServiceUnitValues.includes(o.value)),
  )

  const computeGroupSelection = (roles: TOption[], serviceUnits: TOption[]): Set<number> => {
    if (roles.length === 0 && serviceUnits.length === 0) return new Set()
    const roleValues = roles.map((o) => o.value)
    const serviceUnitValues = serviceUnits.map((o) => o.value)
    return new Set(
      contacts
        .filter((c) => {
          const roleMatch = roles.length === 0 || roleValues.includes(c.role ?? '')
          const serviceMatch = serviceUnits.length === 0 || serviceUnitValues.includes(c.serviceUnit ?? '')
          return roleMatch && serviceMatch
        })
        .map((c) => c.id),
    )
  }

  const [groupSelectedIds, setGroupSelectedIds] = useState<Set<number>>(() =>
    computeGroupSelection(
      roleOptions.filter((o) => initialRoleValues.includes(o.value)),
      serviceUnitOptions.filter((o) => initialServiceUnitValues.includes(o.value)),
    ),
  )
  const [manualSelectedIds, setManualSelectedIds] = useState<Set<number>>(() => {
    const initRoles = roleOptions.filter((o) => initialRoleValues.includes(o.value))
    const initServices = serviceUnitOptions.filter((o) => initialServiceUnitValues.includes(o.value))
    const initGroupIds = computeGroupSelection(initRoles, initServices)
    return new Set(initialSelectedIds.filter((id) => !initGroupIds.has(id)))
  })
  const [searchQuery, setSearchQuery] = useState('')
  const [sort, setSort] = useState<TSortState>({ key: 'Lastname', descOrder: false })
  const [currentPage, setCurrentPage] = useState(1)
  const [newlySelectedIds, setNewlySelectedIds] = useState<Set<number>>(new Set())
  const batchCounterRef = useRef(0)
  const [batchOrderMap, setBatchOrderMap] = useState<Map<number, number>>(new Map())

  useEffect(() => {
    if (newlySelectedIds.size === 0) return
    const timer = setTimeout(() => setNewlySelectedIds(new Set()), 1500)
    return () => clearTimeout(timer)
  }, [newlySelectedIds])

  const selectedIds = new Set([...groupSelectedIds, ...manualSelectedIds])

  const filteredContacts = useMemo(
    () =>
      contacts.filter((contact) => {
        if (searchQuery === '') return true
        return `${contact.firstname ?? ''} ${contact.lastname ?? ''}`
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      }),
    [contacts, searchQuery],
  )

  const sortedContacts = useMemo(() => {
    const allSelectedIds = new Set([...groupSelectedIds, ...manualSelectedIds])

    return [...filteredContacts].sort((a, b) => {
      // Primary: batch order — higher number = more recently selected = appears first.
      // Filter-selected contacts carry a batch number >= 1.
      // Manually selected contacts without a batch fall back to 0 (above unselected).
      // Unselected contacts get -1.
      const aBatch = batchOrderMap.get(a.id) ?? (allSelectedIds.has(a.id) ? 0 : -1)
      const bBatch = batchOrderMap.get(b.id) ?? (allSelectedIds.has(b.id) ? 0 : -1)
      if (aBatch !== bBatch) return bBatch - aBatch

      // Secondary: current sort key
      if (sort.key === 'Selected') {
        const aSelected = allSelectedIds.has(a.id) ? 0 : 1
        const bSelected = allSelectedIds.has(b.id) ? 0 : 1
        const cmp = aSelected - bSelected
        return sort.descOrder ? -cmp : cmp
      }
      const aVal =
        (sort.key === 'Lastname'
          ? a.lastname
          : sort.key === 'Firstname'
            ? a.firstname
            : sort.key === 'Role'
              ? a.role
              : a.serviceUnit) ?? ''
      const bVal =
        (sort.key === 'Lastname'
          ? b.lastname
          : sort.key === 'Firstname'
            ? b.firstname
            : sort.key === 'Role'
              ? b.role
              : b.serviceUnit) ?? ''
      const cmp = aVal.localeCompare(bVal, locale)
      return sort.descOrder ? -cmp : cmp
    })
  }, [filteredContacts, sort, groupSelectedIds, manualSelectedIds, locale, batchOrderMap])

  const paginatedContacts = sortedContacts.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  )

  const handleSort = (key: TContactHeaderName) => {
    setSort((prev) => (prev.key === key ? { key, descOrder: !prev.descOrder } : { key, descOrder: false }))
    setCurrentPage(1)
  }

  const handleSearch = (query: string) => {
    setSearchQuery(query)
    setCurrentPage(1)
  }

  const toggleRole = (option: TOption) => {
    const isAdding = !selectedRoleOptions.some((o) => o.value === option.value)
    const newOptions = isAdding
      ? [...selectedRoleOptions, option]
      : selectedRoleOptions.filter((o) => o.value !== option.value)
    setSelectedRoleOptions(newOptions)
    const newGroupIds = computeGroupSelection(newOptions, selectedServiceUnitOptions)
    setGroupSelectedIds(newGroupIds)
    if (isAdding) {
      const currentSelected = new Set([...groupSelectedIds, ...manualSelectedIds])
      const added = new Set([...newGroupIds].filter((id) => !currentSelected.has(id)))
      if (added.size > 0) {
        batchCounterRef.current += 1
        const batchNum = batchCounterRef.current
        setNewlySelectedIds(added)
        setBatchOrderMap((prev) => {
          const next = new Map(prev)
          added.forEach((id) => next.set(id, batchNum))
          return next
        })
        setCurrentPage(1)
      }
    } else {
      setNewlySelectedIds(new Set())
      setBatchOrderMap((prev) => {
        const next = new Map<number, number>()
        prev.forEach((batchNum, id) => {
          if (newGroupIds.has(id)) next.set(id, batchNum)
        })
        return next
      })
    }
  }

  const toggleServiceUnit = (option: TOption) => {
    const isAdding = !selectedServiceUnitOptions.some((o) => o.value === option.value)
    const newOptions = isAdding
      ? [...selectedServiceUnitOptions, option]
      : selectedServiceUnitOptions.filter((o) => o.value !== option.value)
    setSelectedServiceUnitOptions(newOptions)
    const newGroupIds = computeGroupSelection(selectedRoleOptions, newOptions)
    setGroupSelectedIds(newGroupIds)
    if (isAdding) {
      const currentSelected = new Set([...groupSelectedIds, ...manualSelectedIds])
      const added = new Set([...newGroupIds].filter((id) => !currentSelected.has(id)))
      if (added.size > 0) {
        batchCounterRef.current += 1
        const batchNum = batchCounterRef.current
        setNewlySelectedIds(added)
        setBatchOrderMap((prev) => {
          const next = new Map(prev)
          added.forEach((id) => next.set(id, batchNum))
          return next
        })
        setCurrentPage(1)
      }
    } else {
      setNewlySelectedIds(new Set())
      setBatchOrderMap((prev) => {
        const next = new Map<number, number>()
        prev.forEach((batchNum, id) => {
          if (newGroupIds.has(id)) next.set(id, batchNum)
        })
        return next
      })
    }
  }

  const toggleContact = (id: number) => {
    setManualSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const hasFilters = roleOptions.length > 0 || serviceUnitOptions.length > 0

  const allFiltersSelected = hasFilters
    ? selectedRoleOptions.length === roleOptions.length &&
      selectedServiceUnitOptions.length === serviceUnitOptions.length
    : filteredContacts.length > 0 && filteredContacts.every((c) => selectedIds.has(c.id))

  const selectAll = () => {
    if (hasFilters) {
      setSelectedRoleOptions(roleOptions)
      setSelectedServiceUnitOptions(serviceUnitOptions)
      setGroupSelectedIds(computeGroupSelection(roleOptions, serviceUnitOptions))
      setManualSelectedIds(new Set())
    } else {
      setManualSelectedIds((prev) => {
        const next = new Set(prev)
        filteredContacts.forEach((c) => next.add(c.id))
        return next
      })
    }
  }

  const deselectAll = () => {
    setSelectedRoleOptions([])
    setSelectedServiceUnitOptions([])
    setGroupSelectedIds(new Set())
    setManualSelectedIds(new Set())
    setBatchOrderMap(new Map())
    setNewlySelectedIds(new Set())
  }

  const rows = paginatedContacts.map((contact) => ({
    _isNew: newlySelectedIds.has(contact.id),
    Selected: {
      observedData: selectedIds.has(contact.id) ? 0 : 1,
      component: (
        <CurecallCheckbox
          checked={selectedIds.has(contact.id)}
          onChange={() => toggleContact(contact.id)}
          aria-label={[contact.firstname, contact.lastname].filter(Boolean).join(' ') || contact.phone}
        />
      ),
    },
    Lastname: contact.lastname ?? '—',
    Firstname: contact.firstname ?? '—',
    Role: contact.role ?? '—',
    ServiceUnit: contact.serviceUnit ?? '—',
  }))

  const headers: CurecallListHeaders<TContactRow> = [
    {
      label: t('pages.hr.campaigns.new.contacts.selected'),
      key: 'Selected',
      isSorted: true,
      onHeaderClick: () => handleSort('Selected'),
    },
    {
      label: t('pages.hr.contacts.table.lastname'),
      key: 'Lastname',
      isSorted: true,
      onHeaderClick: () => handleSort('Lastname'),
    },
    {
      label: t('pages.hr.contacts.table.firstname'),
      key: 'Firstname',
      isSorted: true,
      onHeaderClick: () => handleSort('Firstname'),
    },
    {
      label: t('pages.hr.contacts.table.role'),
      key: 'Role',
      isSorted: true,
      onHeaderClick: () => handleSort('Role'),
    },
    {
      label: t('pages.hr.contacts.table.service-unit'),
      key: 'ServiceUnit',
      isSorted: true,
      onHeaderClick: () => handleSort('ServiceUnit'),
    },
  ]

  return (
    <div className="c-flex c-flex-col c-gap-4 c-flex-1 c-min-h-0">
      <div className="c-flex c-gap-4 c-h-[calc(100dvh-13rem)] c-min-h-0">
        {/* Left panel — filter lists */}
        {hasFilters && (
          <div className="c-w-72 c-shrink-0 c-flex c-flex-col c-gap-4 c-min-h-0">
            {roleOptions.length > 0 && (
              <FilterList
                label={t('pages.hr.campaigns.new.contacts.filter-role')}
                options={roleOptions}
                selectedOptions={selectedRoleOptions}
                searchPlaceholder={t('pages.hr.campaigns.new.contacts.filter-role-search')}
                onToggle={toggleRole}
              />
            )}
            {serviceUnitOptions.length > 0 && (
              <FilterList
                label={t('pages.hr.campaigns.new.contacts.filter-service-unit')}
                options={serviceUnitOptions}
                selectedOptions={selectedServiceUnitOptions}
                searchPlaceholder={t('pages.hr.campaigns.new.contacts.filter-service-unit-search')}
                onToggle={toggleServiceUnit}
              />
            )}
          </div>
        )}

        {/* Right panel — toolbar + list */}
        <div className="c-flex c-flex-col c-gap-3 c-flex-1 c-min-h-0">
          {/* Toolbar */}
          <div className="c-flex c-items-center c-gap-3">
            <CurecallButton
              type="button"
              appearance={ECurecallButtonAppearance['PRIMARY-OUTLINED']}
              size={ECurecallSize.SMALL}
              onClick={allFiltersSelected ? deselectAll : selectAll}
            >
              {allFiltersSelected
                ? t('pages.hr.campaigns.new.contacts.deselect-all')
                : t('pages.hr.campaigns.new.contacts.select-all')}
            </CurecallButton>

            <div className="c-flex-1">
              <CurecallInput
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder={t('pages.hr.campaigns.new.contacts.search-placeholder')}
              />
            </div>

            <span className="c-text-sm c-text-grey-500 c-whitespace-nowrap c-shrink-0">
              {t('pages.hr.campaigns.new.contacts.counter', {
                displayed: filteredContacts.length,
                selected: selectedIds.size,
              })}
            </span>
          </div>

          {/* List + pagination */}
          {filteredContacts.length === 0 ? (
            <p className="c-text-grey-500 c-text-sm">{t('pages.hr.campaigns.new.contacts.empty')}</p>
          ) : (
            <>
              <div className="campaign-contacts-list c-overflow-auto c-flex-1 c-min-h-0 c-border c-border-grey-200 c-rounded-lg">
                <CurecallList
                  rowsData={rows}
                  headersData={headers}
                  currentOrder={sort}
                  isSelectedRows={
                    newlySelectedIds.size > 0 ? { matchingHeader: '_isNew', matchingValue: true } : undefined
                  }
                />
              </div>
              {sortedContacts.length > ITEMS_PER_PAGE && (
                <CurecallPaginationBar
                  totalItems={sortedContacts.length}
                  itemsPerPage={ITEMS_PER_PAGE}
                  currentPage={currentPage}
                  numberOfVisiblePages={7}
                  pageLabel={t('common.actions.page')}
                  pagesLabel={t('common.actions.pages')}
                  onClick={(page) => setCurrentPage(page)}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="c-flex c-items-center c-justify-between c-pt-2">
        <CurecallButton
          type="button"
          appearance={ECurecallButtonAppearance['PRIMARY-OUTLINED']}
          size={ECurecallSize.MEDIUM}
          onClick={onBack}
        >
          {t('pages.hr.campaigns.new.contacts.back')}
        </CurecallButton>

        <CurecallButton
          type="button"
          appearance={ECurecallButtonAppearance.PRIMARY}
          size={ECurecallSize.MEDIUM}
          isDisabled={selectedIds.size === 0}
          onClick={() =>
            onNext(
              [...selectedIds],
              selectedRoleOptions.map((o) => o.value),
              selectedServiceUnitOptions.map((o) => o.value),
            )
          }
        >
          {t('pages.hr.campaigns.new.contacts.next-with-count', { count: selectedIds.size })}
        </CurecallButton>
      </div>
    </div>
  )
}
